/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\GuiTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { Image, Settings } from 'lucide-react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import Select from '../../../components/ui/select';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { SecretInput } from '../../../components/ui/SecretField';
import { useI18n } from '../../../hooks/useI18n';
import { getOptions } from '../utils/extractOptions';
import type * as api from '../api';

interface GuiTabProps {
  selectedModel: api.DeviceModel;
  control: Control<api.Template>;
  errors: FieldErrors<api.Template>;
  previewUrl: string | null;
  selectedWallpaperFile: File | null;
  setSelectedWallpaperFile: (file: File | null) => void;
}

export function GuiTab({
  selectedModel,
  control,
  errors,
  previewUrl,
  selectedWallpaperFile,
  setSelectedWallpaperFile,
}: GuiTabProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('provisioningTemplates.tabs.gui', 'GUI Settings')} icon={<Settings className="h-4 w-4" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
        {/* Left Column: Form Fields */}
        <div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-3 items-center">
          <Label className="text-left">{t('provisioningTemplates.adminPassword', 'Admin Password')}</Label>
          <Controller name="adminPassword" control={control}
            render={({ field }) => (
              <SecretInput id="adminPassword" value={field.value || ''} onChange={field.onChange}
                placeholder="��������" className={errors.adminPassword ? 'border-red-500' : ''} />
            )} />

          {getOptions(selectedModel.timeFormat).length > 0 && (
            <>
              <Label className="text-left">{t('provisioningTemplates.timeFormat', 'Time Format')}</Label>
              <Controller name="timeFormat" control={control}
                render={({ field }) => (
                  <Select value={field.value || ''} onValueChange={field.onChange}
                    options={getOptions(selectedModel.timeFormat)} placeholder={t('common.select', 'Select...')} />
                )} />
            </>
          )}

          {getOptions(selectedModel.dateFormat).length > 0 && (
            <>
              <Label className="text-left">{t('provisioningTemplates.dateFormat', 'Date Format')}</Label>
              <Controller name="dateFormat" control={control}
                render={({ field }) => (
                  <Select value={field.value || ''} onValueChange={field.onChange}
                    options={getOptions(selectedModel.dateFormat)} placeholder={t('common.select', 'Select...')} />
                )} />
            </>
          )}

          {getOptions(selectedModel.summerTime).length > 0 && (
            <>
              <Label className="text-left">{t('provisioningTemplates.summerTime', 'Summer Time')}</Label>
              <Controller name="summerTime" control={control}
                render={({ field }) => (
                  <Select value={field.value || ''} onValueChange={field.onChange}
                    options={getOptions(selectedModel.summerTime)} placeholder={t('common.select', 'Select...')} />
                )} />
            </>
          )}

          {getOptions(selectedModel.daylightSavings).length > 0 && (
            <>
              <Label className="text-left">{t('provisioningTemplates.daylightSavings', 'Daylight Savings')}</Label>
              <Controller name="daylightSavings" control={control}
                render={({ field }) => (
                  <Select value={field.value || ''} onValueChange={field.onChange}
                    options={getOptions(selectedModel.daylightSavings)} placeholder={t('common.select', 'Select...')} />
                )} />
            </>
          )}
        </div>

        {/* Right Column: Wallpaper Upload */}
        {selectedModel.wallpaper && (
          <div className="col-span-2 h-full flex flex-col">
            <Label className="text-left mb-2">{t('provisioningTemplates.wallpaper', 'Phone Wallpaper')}</Label>
            <div className="flex-1 border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center text-center bg-muted/5 hover:bg-muted/10 transition-colors relative min-h-[200px]">
              {previewUrl ? (
                <img src={previewUrl} alt="Wallpaper Preview"
                  className="h-24 w-auto max-w-[80%] object-contain rounded-md mb-3 border border-border bg-black/5 shadow-sm" />
              ) : (
                <Image className="h-10 w-10 text-muted-foreground/50 mb-3" />
              )}
              <div className="space-y-1 mb-4">
                <p className="text-sm font-medium text-foreground">
                  {selectedWallpaperFile ? selectedWallpaperFile.name : t('provisioningTemplates.uploadWallpaper', 'Upload Wallpaper')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedWallpaperFile
                    ? `${(selectedWallpaperFile.size / 1024).toFixed(2)} KB`
                    : t('provisioningTemplates.dragDrop', 'Drag and drop or select a file')}
                </p>
              </div>
              <div className="w-full max-w-xs space-y-3 z-20">
                <div className="relative">
                  <input type="file" className="hidden" id="wallpaper-upload" accept="image/*"
                    onChange={(e) => { if (e.target.files?.[0]) setSelectedWallpaperFile(e.target.files[0]); }} />
                  <label htmlFor="wallpaper-upload"
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm cursor-pointer hover:bg-primary/90 transition-colors">
                    {t('common.selectFile', 'Select File')}
                  </label>
                </div>
              </div>
              {selectedModel.wallpaper && (
                <p className="absolute bottom-2 text-[10px] text-muted-foreground">
                  {t('provisioningTemplates.wallpaperSupported', 'Supported')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </FormBox>
  );
}
